import React from "react";
import { Grid, TextField, Typography } from "@mui/material";
import SwitchConfig, { SwitchConfigProps } from './SwitchConfig';
import { makeStyles } from "@mui/styles";

interface BeforeAfterSwitchConfigProps extends SwitchConfigProps {
    beforeValueName: string,
    beforeValue: undefined | number,
    defaultBeforeValue: number,
    afterValueName: string,
    afterValue: undefined | number,
    defaultAfterValue: number,
    complex?: boolean,
    beforeError: undefined | string,
    afterError: undefined | string,
    onError: (key: string, value: string) => void,
}

export default function BeforeAfterSwitchConfig(
    {
        name,
        label,
        description,
        checked,
        onChange,
        beforeValueName,
        beforeValue,
        defaultBeforeValue,
        afterValueName,
        afterValue,
        defaultAfterValue,
        complex,
        beforeError,
        afterError,
        onError,
        restProps
    }: BeforeAfterSwitchConfigProps
) {
    const useStyles = makeStyles({
        inputRoot: {
            '& .MuiOutlinedInput-root': {
                width: '69px',
                height: '25px',
                textAlign: 'center',
                fontSize: 12,
                '& fieldset': {
                    borderRadius: '24px',
                    borderColor: '#F1F0F5',
                },
            },
            '& .MuiOutlinedInput-root.Mui-disabled': {
                '& fieldset': {
                    borderColor: '#F1F0F5',
                    backgroundColor: '#F1F0F5',
                }
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
                '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: 1
                },
            }
        },
    });
    const classes = useStyles();

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const re = /^[0-9,\b][0-9,\b]*$/;

        if (event.target.value === '') {
            onChange(event.target.name, '');
            onError(event.target.name, 'This field is required.');
        } else if (re.test(event.target.value)) {
            if (Number(event.target.value) >= defaultBeforeValue 
                && Number(event.target.value) <= defaultAfterValue) {
                onChange(event.target.name, Number(event.target.value));
                onError(event.target.name, '');
            }
        }
    }

    const handleSwitchChange = (ConfigTypeKey: string, AssignedValue: any) => {
        if (complex) {
            if (AssignedValue) {
                onChange(beforeValueName, defaultBeforeValue, afterValueName, defaultBeforeValue);
            } else {
                onChange(beforeValueName, (defaultAfterValue + 1), afterValueName, (defaultBeforeValue - 1));
            }
        } else {
            onChange(ConfigTypeKey, AssignedValue);
        }
    }

    return (
        <Grid container sx={{ alignItems: 'center' }}>
            <Grid item xs={12}
                sx={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: {
                        xs: "flex-end",
                        lg: "flex-start"
                    },
                    flexDirection: {
                        xs: "column",
                        md: "row"
                    }
                }}
            >
                <SwitchConfig
                    name={name}
                    label={label}
                    description={description}
                    checked={checked}
                    onChange={handleSwitchChange}
                    {...restProps}
                />

                <Typography 
                    sx={{ 
                        fontFamily: 'Work Sans',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        mr: '9px',
                    }}
                >
                    {`${label} are allowed from`}
                </Typography>
                <TextField
                    name={beforeValueName}
                    value={beforeValue ?? defaultBeforeValue}
                    onChange={handleTextChange}
                    classes={{
                        root: classes.inputRoot,
                    }}
                    inputProps={{ style: { textAlign: "center" } }}
                    error={!!beforeError}
                    disabled={beforeValue === (defaultAfterValue + 1)}
                />
                <Typography 
                    sx={{ 
                        fontFamily: 'Work Sans',
                        fontWeight: 400,
                        fontSize: '12px',
                        lineHeight: '22px',
                        ml: '7px',
                        mr: '8px',
                    }}
                >
                    through
                </Typography>
                <TextField
                    name={afterValueName}
                    value={afterValue ?? defaultAfterValue}
                    onChange={handleTextChange}
                    classes={{
                        root: classes.inputRoot,
                    }}
                    inputProps={{ style: { textAlign: "center" } }}
                    error={!!afterError}
                    disabled={afterValue === (defaultBeforeValue - 1)}
                />
            </Grid>
            {(beforeError || afterError) && (
                <React.Fragment>
                    <Grid item xs={12} lg={4}></Grid>
                    <Grid item xs={12} lg={8} sx={{ 
                        textAlign: 'center', 
                        marginTop: { xs: 1, lg: -1 }
                    }}>
                        <Typography 
                            sx={{ 
                                fontFamily: 'Work Sans',
                                fontWeight: 400,
                                fontSize: '12px',
                                lineHeight: '22px',
                                color: '#ff0000',
                            }}
                        >
                            Input fields are required.
                        </Typography>
                    </Grid>
                </React.Fragment>
            )}
        </Grid>
    )
}
